/**
 * Driver phát âm thanh tiếng Việt chất lượng cao (Vietnamese High-Fidelity Audio Driver)
 * Tuân thủ tiêu chuẩn GDD Mục 23, 24, 27, 45, 48 & 83:
 * - Hỗ trợ đa dạng giọng đọc AI: Giọng Nữ (Chuẩn trong trẻo) và Giọng Nam (Trầm ấm).
 * - Sử dụng HTML5 Audio kết hợp Edge Service /api/tts phát âm chuẩn ngữ điệu tiếng Việt 100% có dấu.
 * - Hỗ trợ Prefetching câu tiếp theo để loại bỏ khoảng lặng giữa các câu (Audio Gap Prevention).
 * - Bộ nhớ đệm Audio Cache (LRU/Memory) giúp phát lại câu tức thì 0ms mà không tốn mạng.
 * - Hỗ trợ thanh kéo điều chỉnh tốc độ mượt mà realtime.
 */
export class VietnameseAudioDriver {
  constructor() {
    this.currentAudio = null;
    this.speed = 1.0;
    this.volume = 1.0;
    this.voice = 'ai-female'; // 'ai-female' | 'ai-male'
    this.isPaused = false;

    // Web Audio API Context phục vụ xử lý hiệu ứng âm thanh (Giọng Nam trầm ấm)
    this.audioCtx = null;
    this.bassFilter = null;
    this.trebleFilter = null;

    // Bộ nhớ đệm Runtime Cache lưu trữ Object URL của các câu gần nhất
    this.audioCache = new Map(); // key: text, value: { blobUrl, timestamp }
    this.maxCacheSize = 30; // Giới hạn kích thước cache theo LRU (Mục 28 GDD)

    // Prefetch map cho câu kế tiếp
    this.prefetchCache = new Map();
  }

  /**
   * Thiết lập giọng đọc AI ('ai-female' hoặc 'ai-male')
   * @param {string} voiceId 
   */
  setVoice(voiceId) {
    this.voice = voiceId || 'ai-female';
    console.log('[VietnameseAudioDriver] Đã chuyển sang giọng đọc:', this.voice);
  }

  /**
   * Tạo key hash rút gọn từ nội dung câu đọc
   * @param {string} text 
   * @returns {string}
   */
  getCacheKey(text) {
    if (!text || typeof text !== 'string') return '';
    return text.trim().toLowerCase();
  }

  /**
   * Tải trước âm thanh cho câu kế tiếp trong nền để tránh khoảng trễ (Prefetch)
   * @param {string} nextText 
   */
  async prefetch(nextText) {
    if (!nextText || typeof nextText !== 'string') return;
    const key = this.getCacheKey(nextText);
    if (!key || this.audioCache.has(key) || this.prefetchCache.has(key)) return;

    try {
      this.prefetchCache.set(key, true);
      const url = `/api/tts?text=${encodeURIComponent(nextText.trim())}&lang=vi`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Prefetch HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      this.saveToCache(key, blobUrl);
      this.prefetchCache.delete(key);
    } catch (e) {
      this.prefetchCache.delete(key);
    }
  }

  /**
   * Lưu Blob URL vào bộ nhớ đệm có áp dụng cơ chế LRU giải phóng phần tử cũ nhất
   */
  saveToCache(key, blobUrl) {
    if (this.audioCache.size >= this.maxCacheSize) {
      let oldestKey = null;
      let oldestTime = Infinity;
      for (const [k, item] of this.audioCache.entries()) {
        if (item.timestamp < oldestTime) {
          oldestTime = item.timestamp;
          oldestKey = k;
        }
      }
      if (oldestKey) {
        const oldItem = this.audioCache.get(oldestKey);
        if (oldItem && oldItem.blobUrl) {
          try { URL.revokeObjectURL(oldItem.blobUrl); } catch (_) {}
        }
        this.audioCache.delete(oldestKey);
      }
    }

    this.audioCache.set(key, { blobUrl, timestamp: Date.now() });
  }

  /**
   * Lấy Audio URL (ưu tiên từ Cache hoặc gọi /api/tts)
   * @param {string} text 
   * @returns {Promise<string>}
   */
  async getAudioUrl(text) {
    const key = this.getCacheKey(text);
    if (this.audioCache.has(key)) {
      const item = this.audioCache.get(key);
      item.timestamp = Date.now();
      return item.blobUrl;
    }

    const endpoint = `/api/tts?text=${encodeURIComponent(text.trim())}&lang=vi`;
    const response = await fetch(endpoint);
    if (!response.ok) {
      throw new Error(`Không thể lấy âm thanh tiếng Việt (HTTP ${response.status})`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    this.saveToCache(key, blobUrl);
    return blobUrl;
  }

  /**
   * Khởi tạo bộ lọc âm trầm cho giọng Nam qua Web Audio API
   */
  setupAudioFilters(audio) {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      if (!this.audioCtx) {
        this.audioCtx = new AudioContextClass();
      }

      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }

      const source = this.audioCtx.createMediaElementSource(audio);

      if (this.voice === 'ai-male') {
        // Bộ lọc tăng dải trầm (Low-shelf) tạo giọng nam dày dặn
        const bass = this.audioCtx.createBiquadFilter();
        bass.type = 'lowshelf';
        bass.frequency.value = 350;
        bass.gain.value = 7.5;

        // Bộ lọc giảm dải bổng (High-shelf) làm ấm tiếng
        const treble = this.audioCtx.createBiquadFilter();
        treble.type = 'highshelf';
        treble.frequency.value = 3200;
        treble.gain.value = -5.0;

        source.connect(bass);
        bass.connect(treble);
        treble.connect(this.audioCtx.destination);
      } else {
        // Giọng nữ phát tự nhiên trực tiếp
        source.connect(this.audioCtx.destination);
      }
    } catch (e) {
      // Nếu trình duyệt chặn MediaElementSource trên audio object tái sử dụng, fallback phát bình thường
      console.debug('[VietnameseAudioDriver] Bỏ qua Web Audio filters:', e.message);
    }
  }

  /**
   * Phát một câu văn bản tiếng Việt
   * @param {string} text Nội dung cần đọc
   * @param {() => void} onEnd Callback kết thúc câu
   * @param {(err: any) => void} onError Callback khi gặp lỗi
   * @returns {Promise<void>}
   */
  async speak(text, onEnd, onError) {
    if (!text || typeof text !== 'string' || !text.trim()) {
      if (typeof onEnd === 'function') onEnd();
      return;
    }

    this.stop();
    this.isPaused = false;

    try {
      const audioUrl = await this.getAudioUrl(text);
      const audio = new Audio(audioUrl);

      // Cập nhật tốc độ đọc và âm lượng
      audio.playbackRate = this.speed;
      audio.volume = this.volume;
      this.currentAudio = audio;

      // Áp dụng bộ lọc âm thanh nếu là giọng nam
      if (this.voice === 'ai-male') {
        this.setupAudioFilters(audio);
      }

      audio.onended = () => {
        this.currentAudio = null;
        if (typeof onEnd === 'function') onEnd();
      };

      audio.onerror = (e) => {
        console.warn('[VietnameseAudioDriver] Lỗi phát Audio:', e);
        this.currentAudio = null;
        if (typeof onError === 'function') onError(e);
      };

      await audio.play();
    } catch (err) {
      console.error('[VietnameseAudioDriver] Lỗi tổng hợp/phát:', err);
      if (typeof onError === 'function') onError(err);
    }
  }

  /**
   * Tạm dừng đọc
   */
  pause() {
    if (this.currentAudio && !this.currentAudio.paused) {
      this.currentAudio.pause();
      this.isPaused = true;
    }
  }

  /**
   * Tiếp tục đọc
   */
  resume() {
    if (this.currentAudio && this.currentAudio.paused) {
      this.currentAudio.play().catch(e => console.warn('[VietnameseAudioDriver] Resume error:', e));
      this.isPaused = false;
    }
  }

  /**
   * Dừng đọc hoàn toàn
   */
  stop() {
    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio.onended = null;
        this.currentAudio.onerror = null;
      } catch (_) {}
      this.currentAudio = null;
    }
    this.isPaused = false;
  }

  /**
   * Thiết lập tốc độ đọc mượt mà từ thanh trượt (0.5x đến 2.5x)
   */
  setSpeed(speed) {
    this.speed = Math.max(0.5, Math.min(2.5, speed));
    if (this.currentAudio) {
      this.currentAudio.playbackRate = this.speed;
    }
  }

  /**
   * Thiết lập âm lượng (0.0 đến 1.0)
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.currentAudio) {
      this.currentAudio.volume = this.volume;
    }
  }

  /**
   * Dọn dẹp tài nguyên khi hủy bỏ
   */
  destroy() {
    this.stop();
    for (const item of this.audioCache.values()) {
      if (item && item.blobUrl) {
        try { URL.revokeObjectURL(item.blobUrl); } catch (_) {}
      }
    }
    this.audioCache.clear();
    this.prefetchCache.clear();
  }
}
